import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import type { IndexablePath } from "../components/Seo";
import { useLang, useLp } from "../lib/i18n";
import { trustCopy } from "../lib/translations/pages/trust";

export function Trust() {
  const lang = useLang();
  const lp = useLp();
  const t = trustCopy[lang];

  // Each language tree has a distinct slug for the trust page.
  const canonicalPath: IndexablePath =
    lang === "pt"
      ? "/onde-seus-dados-vao"
      : lang === "es"
        ? "/donde-van-tus-datos"
        : "/trust";

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title={t.seo.title}
        description={t.seo.description}
        path={canonicalPath}
        alternateLangs={["en", "pt", "es"]}
      />
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="border-b border-margin dark:border-paper/10">
          <div className="mx-auto max-w-3xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
              {t.eyebrow}
            </p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink dark:text-paper">
              {t.h1}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-ink/75 dark:text-paper/75">
              {t.intro}
            </p>
          </div>
        </section>

        {/* Section 1 — What stays on your machine */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.sections.local.heading}
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                {t.sections.local.body}
              </p>
              <ul className="mt-8 space-y-3">
                {t.sections.local.items.map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-[2px] flex-shrink-0 w-5 h-5 rounded-full border border-ink/20 dark:border-paper/20 flex items-center justify-center"
                    >
                      <span className="block w-2 h-2 rounded-full bg-ink/40 dark:bg-paper/40" />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>

        {/* Section 2 — What we send to the cloud */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.sections.cloud.heading}
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                {t.sections.cloud.body}
              </p>
              <div className="mt-10 space-y-5">
                {t.sections.cloud.chain.map((entry) => (
                  <div
                    key={entry.label}
                    className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-5 dark:border-paper/12 dark:bg-paper/[0.02]"
                  >
                    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
                      {entry.label}
                    </p>
                    <p className="mt-2 text-[14px] font-medium text-ink dark:text-paper">
                      {entry.primary}
                    </p>
                    {entry.fallback1 && (
                      <p className="mt-1 text-[13px] text-ink/65 dark:text-paper/65">
                        {entry.fallback1}
                      </p>
                    )}
                    {entry.fallback2 && (
                      <p className="mt-1 text-[13px] text-ink/65 dark:text-paper/65">
                        {entry.fallback2}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        {/* Section 3 — Subprocessors table */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.sections.subprocessors.heading}
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80 max-w-3xl">
                {t.sections.subprocessors.body}
              </p>
              <div className="mt-10 overflow-x-auto rounded-2xl border border-ink/10 dark:border-paper/12">
                <table className="w-full text-left text-[13px]">
                  <thead>
                    <tr className="border-b border-ink/10 dark:border-paper/12 bg-ink/[0.02] dark:bg-paper/[0.02]">
                      <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55 font-normal">
                        {t.sections.subprocessors.columns.name}
                      </th>
                      <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55 font-normal">
                        {t.sections.subprocessors.columns.purpose}
                      </th>
                      <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55 font-normal">
                        {t.sections.subprocessors.columns.retention}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink/10 dark:divide-paper/12">
                    {t.sections.subprocessors.rows.map((row) => (
                      <tr
                        key={row.name}
                        className="hover:bg-ink/[0.01] dark:hover:bg-paper/[0.01] transition-colors"
                      >
                        <td className="px-5 py-4 font-medium text-ink dark:text-paper align-top whitespace-nowrap">
                          {row.name}
                        </td>
                        <td className="px-5 py-4 text-ink/75 dark:text-paper/75 align-top">
                          {row.purpose}
                        </td>
                        <td className="px-5 py-4 text-ink/65 dark:text-paper/65 align-top">
                          {row.retention}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Section 4 — What we do NOT do */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.sections.notDoing.heading}
              </h2>
              <ul className="mt-8 space-y-4">
                {t.sections.notDoing.items.map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 text-[15px] leading-relaxed text-ink/80 dark:text-paper/80"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-[3px] flex-shrink-0 text-ink/40 dark:text-paper/40 font-mono text-[11px]"
                    >
                      ✕
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>

        {/* Section 5 — Links */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.sections.links.heading}
              </h2>
              <ul className="mt-8 space-y-3">
                {t.sections.links.items.map((link) => {
                  const isExternal = link.href.startsWith("http");
                  return (
                    <li key={link.href}>
                      {isExternal ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[15px] text-ink/80 dark:text-paper/80 underline underline-offset-2 hover:text-ink dark:hover:text-paper transition-colors"
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link
                          to={lp + link.href}
                          className="text-[15px] text-ink/80 dark:text-paper/80 underline underline-offset-2 hover:text-ink dark:hover:text-paper transition-colors"
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
        </Reveal>

        {/* Section 6 — Contact */}
        <Reveal>
          <section>
            <div className="mx-auto max-w-3xl px-6 py-24 text-center">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.sections.contact.heading}
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-ink/70 dark:text-paper/70">
                {t.sections.contact.body}
              </p>
              <div className="mt-9">
                <a
                  href="mailto:contact@minutapp.tech"
                  className="inline-flex items-center rounded-full bg-ink px-6 py-3 text-sm font-medium text-paper hover:bg-ink/90 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
                >
                  {t.sections.contact.cta}
                </a>
              </div>
            </div>
          </section>
        </Reveal>
      </main>

      <Footer />
    </div>
  );
}
